import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  useMediaQuery,
  List,
  Typography,
  ListItem,
  ListItemButton,
  Collapse,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { SidebarWidth } from '../../../assets/global/Theme-variable';
import LogoIcon from '../logo/LogoIcon';
import { getMenuitems } from './Menuitems';
import Scrollbar from '../../../components/custom-scroll/Scrollbar';

const Sidebar = ({ isMobileSidebarOpen, onSidebarClose, isSidebarOpen, lst_menu, loggedUser }) => {
  const [open, setOpen] = useState(true);
  const [Menuitems, setMenuitems] = useState([]);
  const { pathname } = useLocation();

  const pathDirect = pathname;
  const pathWithoutLastPart = pathname.slice(0, pathname.lastIndexOf('/'));
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const handleClick = useCallback((index) => {
    setOpen((prevOpen) => (prevOpen === index ? false : index));
  }, []);

  useEffect(() => {
    if (lst_menu) {
      setMenuitems(getMenuitems(lst_menu, loggedUser));
    }
  }, [lst_menu, loggedUser]);

  // Expand matching menu item on initial load or path change
  useEffect(() => {
    if (Array.isArray(Menuitems)) {
      Menuitems.forEach((item, index) => {
        if (item.children) {
          const isChildActive = item.children.some((child) => child.href === pathDirect);
          if (isChildActive || pathWithoutLastPart === item.href) {
            setOpen(index);
          }
        }
      });
    }
  }, [pathname, Menuitems, pathDirect, pathWithoutLastPart]);

  const SidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header / Logo Bar */}
      <Box sx={{ p: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LogoIcon />
      </Box>

      <Divider sx={{ mx: 2, borderColor: 'divider', opacity: 0.6 }} />

      {/* Main Navigation Items */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Scrollbar style={{ height: '100%' }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <List component="nav" disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {Menuitems.map((item, index) => {
                // {/******** SubHeader **********/ }
                if (item.subheader) {
                  return (
                    <Box key={item.subheader} sx={{ mt: 2.5, mb: 1, px: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          color: 'text.secondary',
                          fontSize: '0.68rem',
                          display: 'block',
                        }}
                      >
                        {item.subheader}
                      </Typography>
                    </Box>
                  );
                }

                // {/******** Parent Menu with Children **********/ }
                if (item.children) {
                  const isParentSelected =
                    pathWithoutLastPart === item.href ||
                    item.children.some((child) => child.href === pathDirect);
                  const isExpanded = open === index;

                  return (
                    <React.Fragment key={item.title}>
                      <ListItemButton
                        onClick={() => handleClick(index)}
                        selected={isParentSelected}
                        sx={{
                          borderRadius: 1.5,
                          py: 1,
                          px: 1.5,
                          transition: 'all 0.15s ease-in-out',
                          bgcolor: isParentSelected
                            ? (t) => alpha(t.palette.primary.main, 0.08)
                            : 'transparent',
                          color: isParentSelected ? 'primary.main' : 'text.primary',
                          borderLeft: isParentSelected ? '3px solid' : '3px solid transparent',
                          borderColor: isParentSelected ? 'primary.main' : 'transparent',
                          '&:hover': {
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: isParentSelected ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          <FeatherIcon icon={item.icon} width="18" height="18" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: isParentSelected ? 700 : 500,
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            color: isParentSelected ? 'primary.main' : 'text.secondary',
                            transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        >
                          <FeatherIcon icon="chevron-right" size="16" />
                        </Box>
                      </ListItemButton>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 2, pt: 0.5, pb: 0.5 }}>
                          {item.children.map((child) => {
                            const isChildSelected = pathDirect === child.href;

                            return (
                              <ListItemButton
                                key={child.title}
                                component={NavLink}
                                to={child.href}
                                onClick={onSidebarClose}
                                selected={isChildSelected}
                                sx={{
                                  borderRadius: 1.5,
                                  py: 0.75,
                                  px: 1.5,
                                  mb: 0.5,
                                  transition: 'all 0.15s ease-in-out',
                                  bgcolor: isChildSelected
                                    ? (t) => alpha(t.palette.primary.main, 0.12)
                                    : 'transparent',
                                  color: isChildSelected ? 'primary.main' : 'text.secondary',
                                  '&:hover': {
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                                    color: 'primary.main',
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 28,
                                    color: isChildSelected ? 'primary.main' : 'text.secondary',
                                  }}
                                >
                                  <FeatherIcon icon={child.icon || 'circle'} width="14" height="14" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.title}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: isChildSelected ? 700 : 400,
                                    fontSize: '0.82rem',
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                }

                // {/******** Single Menu Item **********/ }
                const isSelected = pathDirect === item.href;

                return (
                  <ListItemButton
                    key={item.title}
                    component={NavLink}
                    to={item.href}
                    onClick={onSidebarClose}
                    selected={isSelected}
                    sx={{
                      borderRadius: 1.5,
                      py: 1,
                      px: 1.5,
                      transition: 'all 0.15s ease-in-out',
                      bgcolor: isSelected
                        ? (t) => alpha(t.palette.primary.main, 0.1)
                        : 'transparent',
                      color: isSelected ? 'primary.main' : 'text.primary',
                      borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                        color: 'primary.main',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: isSelected ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      <FeatherIcon icon={item.icon} width="18" height="18" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isSelected ? 700 : 500,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </Scrollbar>
      </Box>

      {/* User Resumé Footer Card */}
      {loggedUser && (
        <Box sx={{ p: 1.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              borderColor: (t) => alpha(t.palette.primary.main, 0.15),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                }}
              >
                {loggedUser?.prenom ? loggedUser.prenom.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {loggedUser?.prenom ? `${loggedUser.prenom} ${loggedUser.nom || ''}` : 'Utilisateur'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {loggedUser?.role || 'Membre'}
                </Typography>
              </Box>
              <Chip
                size="small"
                label="En ligne"
                color="success"
                sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, borderRadius: 1 }}
              />
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        variant="persistent"
        PaperProps={{
          sx: {
            width: SidebarWidth,
            border: '0 !important',
            boxShadow: '1px 0 10px rgba(0,0,0,0.05)',
          },
        }}
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={isMobileSidebarOpen}
      onClose={onSidebarClose}
      PaperProps={{
        sx: {
          width: SidebarWidth,
          border: '0 !important',
        },
      }}
      variant="temporary"
    >
      {SidebarContent}
    </Drawer>
  );
};

Sidebar.propTypes = {
  isMobileSidebarOpen: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  lst_menu: PropTypes.array,
  loggedUser: PropTypes.object,
};

export default Sidebar;
